import { useCallback, useEffect, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
// From the module, not the `@/services` barrel: the barrel also exports
// integration.service, which imports integration-scope, which now imports this file.
import { businessUnitService } from '@/services/businessUnit.service';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { isAuthenticatedAtom, currentUserAtom } from './use-auth';
import type { BusinessUnit } from '@/types';

/**
 * Exported so a brand-scoped gate can wait for it.
 *
 * A query that sends `bu_id` must not answer before the brand is known, or it answers
 * account-wide first and corrects itself a moment later — which is a different answer, not
 * a slower one. See `use-sentinel-dashboard-enabled.ts`.
 */
/**
 * The brand list request itself, shared so the atom and the boot prefetch are ONE request.
 *
 * `main.tsx` starts this before React renders — see the note there. Sharing the options
 * rather than repeating them is what makes that a prefetch of THIS query instead of a
 * second one: react-query matches on the key, so the atom below finds the in-flight
 * promise (or the settled result) instead of issuing its own.
 *
 * `queryFn` is a wrapper rather than the bare reference so it resolves `businessUnitService`
 * at call time — a test that spies on `list` is otherwise bypassed by the reference captured
 * here at module load.
 */
export const businessUnitsQueryFor = (accountId: number | string | null | undefined) => ({
  // THE ACCOUNT IS PART OF THE KEY. Without it one cache entry served every account, and
  // after a move to another account the previous account's brands answered for up to
  // `staleTime`. The stored brand was then found in that stale list, judged valid, and
  // sent: a connect on account 447 went out carrying brand 347, which belongs to 417.
  // The response is account-scoped (machine reads the account from the JWT), so the key
  // has to be too.
  queryKey: ['business-units', String(accountId ?? '')],
  queryFn: () => businessUnitService.list(),
  // Brands change rarely, and this is read on every page load. Kept ON the shared options
  // so `prefetchQuery` honours it too — the client's own default is 0, which would refetch
  // a list the prefetch had only just delivered.
  staleTime: 5 * 60 * 1000,
});

export const businessUnitsAtom = atomWithQuery((get) => ({
  ...businessUnitsQueryFor(get(currentUserAtom)?.accountId),
  enabled: get(isAuthenticatedAtom),
}));

const businessUnitsByAccountAtom = atomWithQuery((get) => ({
  queryKey: ['business-units', 'by-account'],
  queryFn: businessUnitService.listByAccount,
  staleTime: 5 * 60 * 1000,
  enabled: get(isAuthenticatedAtom),
}));

export const useBusinessUnitsByAccount = () => {
  const [{ data }] = useAtom(businessUnitsByAccountAtom);
  return { byAccount: data ?? {} };
};

export const useBusinessUnits = () => {
  const [{ data, isLoading, isError }] = useAtom(businessUnitsAtom);
  return { businessUnits: (data ?? []) as BusinessUnit[], isLoading, isError };
};

/**
 * The selected business unit, and the setter that scopes the whole product to it.
 *
 * ONE SURFACE: localStorage, via the atom. Deliberately NOT mirrored into the URL.
 *
 * It used to be both, so that a scoped view could be shared as a link. Two sources of
 * truth for one selection drift, and every way they drifted was worse than the feature:
 *
 *   - The sidebar navigates with bare paths, so moving between pages dropped the param.
 *     The selection survived (it is atom-backed) but the address bar stopped agreeing.
 *   - The URL winning on load meant the BACK BUTTON could adopt an older `?bu=` and change
 *     the brand nobody had chosen. A selection must only change when someone selects.
 *   - A brand belongs to ONE account. A link carrying `bu=332` opened by someone on
 *     another account scoped them to a brand that account does not own, which returns
 *     empty lists and no error — the same silent failure as carrying a brand across an
 *     account switch.
 *
 * localStorage already survives a refresh and a new tab, which is what the persistence
 * was actually for. Sharing a scoped view is the only thing given up.
 *
 * Not built on `useUrlFilters`: that helper is scoped to a route family and drops its
 * state when you navigate outside it, which is the opposite of a global scope.
 */
/**
 * The brand a selection resolves to when the stored one cannot be used.
 *
 * The account's default brand, falling back to the first active one. There is no "All"
 * any more, so every view is scoped to exactly one brand and this is what it starts on.
 */
const fallbackBusinessUnit = (businessUnits: BusinessUnit[]): BusinessUnit | undefined =>
  businessUnits.find((unit) => unit.isDefault && unit.status === 'active') ??
  businessUnits.find((unit) => unit.status === 'active');

/**
 * The brand in force, as a VALUE rather than something an effect eventually writes.
 *
 * `useBusinessUnit` resolves the same thing, but only once it has mounted and its effect
 * has run — so a query reading the raw stored atom could fire first, in the window after
 * the brand list lands and before the write-back. It would send no `bu_id`, get the
 * ACCOUNT's answer, and then correct itself. For the Sentinel gate that correction is a
 * different dashboard, so it was visible: Analytics v2 painted, then Brand Health replaced
 * it. Deriving it removes the window rather than narrowing it.
 *
 * Null means the same as it does in the store: the brand list has not loaded, or this
 * account genuinely has no active brand. Both read account-wide, which is correct.
 */
export const resolvedBusinessUnitIdAtom = atom((get) => {
  const stored = get(selectedBusinessUnitIdAtom);
  const units = (get(businessUnitsAtom).data ?? []) as BusinessUnit[];
  if (units.length === 0) return stored;
  const match = units.find((unit) => unit.id === stored);
  if (match && match.status === 'active') return match.id;
  return fallbackBusinessUnit(units)?.id ?? null;
});

export const useBusinessUnit = () => {
  const [selectedId, setSelectedId] = useAtom(selectedBusinessUnitIdAtom);
  const { businessUnits, isLoading, isError } = useBusinessUnits();

  /**
   * The stored id is only usable while the account still owns that brand.
   *
   * A brand deleted or deactivated after someone selected it would otherwise leave every
   * list empty and the menu highlighting nothing, with no error to explain it — the
   * selection points at a brand the backend returns no rows for. Falling back to the
   * default brand is the same answer a fresh browser gets.
   */
  const resolvedId = useMemo(() => {
    if (businessUnits.length === 0) return selectedId;
    const stored = businessUnits.find((unit) => unit.id === selectedId);
    if (stored && stored.status === 'active') return stored.id;
    return fallbackBusinessUnit(businessUnits)?.id ?? null;
  }, [businessUnits, selectedId]);

  // Written back so the fallback is not recomputed on every render and survives a reload.
  useEffect(() => {
    if (resolvedId !== null && resolvedId !== selectedId) setSelectedId(resolvedId);
  }, [resolvedId, selectedId, setSelectedId]);

  const setBusinessUnit = useCallback(
    (buId: string) => {
      if (buId === selectedId) return;

      setSelectedId(buId);

      // NOTHING TO INVALIDATE. Every query that sends a brand also carries it in its key
      // (`buScopeKey`), so a brand change produces a different key, which has no cached
      // rows and fetches on its own. The by-id queries — persona, source, workflow
      // template — fetch by primary key and send no brand at all, so the record they
      // return does not depend on the filter.
      //
      // This used to reset the whole cache, from when the keys did NOT carry the brand.
      // Once they did, the reset became a second trigger on top of the key change and
      // every list on the page fetched TWICE per switch, which is what made the Workflows
      // tab hang. It also discarded the previous brand's rows, so switching back refetched
      // instead of being instant.
    },
    [selectedId, setSelectedId],
  );

  return {
    /**
     * The brand every scoped view is looking at. Null ONLY before the brand list has
     * loaded — it no longer means "all business units", which no longer exists.
     */
    buId: resolvedId,
    setBusinessUnit,
    businessUnits,
    isLoading,
    isError,
  };
};
